import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import styles from './visualiser-image-area.module.css';
import { usePinchPan } from '../../../hooks/usePinchPan';

type VisualiserImageData = {
  base64?: string;
  file?: File;
} | null;

type VisualiserImageUpload = {
  userImage: VisualiserImageData;
  editedImage: VisualiserImageData;
} | null;

type VisualiserImageAreaProps = {
  /** Image upload value from `UserInputContext` (via `getImageInput`). */
  value?: VisualiserImageUpload;
  /** Container background color used when there is no image. */
  backgroundColor?: string;
  /** Size constraints for the image area. */
  size?: {
    width: string;
    height: string;
  };
  /** Position of the image area within the Visualiser. */
  position?: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
  /** Container border radius. */
  borderRadius?: string;
  /** Additional styles applied to the image container. */
  style?: React.CSSProperties;
  /** Optional className for the image container. */
  className?: string;
  /** Accessible alt text for the image. */
  alt?: string;
  /** If true, prefers `editedImage` over `userImage`. */
  preferEditedImage?: boolean;
  /** The current scale of the image. */
  scale?: number;
  /** The current X offset of the image. */
  x?: number;
  /** The current Y offset of the image. */
  y?: number;
  /** Callback fired when scale, x, or y is updated by the user. */
  onUpdate?: (updates: { scale?: number; x?: number; y?: number }) => void;
};

/**
 * VisualiserImageArea renders an uploaded image (or an empty state) inside a Visualiser.
 * It is designed to work with `UserInputContext` image methods and supports pinch-to-zoom and panning.
 */
export const VisualiserImageArea = ({
  value,
  backgroundColor = 'var(--color-neutral)',
  size,
  position,
  borderRadius = '12px',
  style,
  className,
  alt = 'Uploaded image',
  preferEditedImage = true,
  scale = 1,
  x = 0,
  y = 0,
  onUpdate
}: VisualiserImageAreaProps) => {
  const selectedImage = useMemo(() => {
    if (!value) return null;
    return preferEditedImage ? value.editedImage ?? value.userImage : value.userImage ?? value.editedImage;
  }, [preferEditedImage, value]);

  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedImage?.file || selectedImage.base64) {
      setObjectUrl(null);
      return;
    }

    const url = URL.createObjectURL(selectedImage.file);
    setObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [selectedImage?.base64, selectedImage?.file]);

  const src = selectedImage?.base64 || objectUrl || '';
  const hasImage = Boolean(src);

  const [active, setActive] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const bind = usePinchPan({
    scale,
    x,
    y,
    onChange: (updates) => {
      if (onUpdate) {
        onUpdate(updates);
      }
    },
    minScale: 0.5,
    maxScale: 5
  });

  // Handle wheel event manually to prevent page zoom.
  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (!hasImage) return;

      event.preventDefault();
      event.stopPropagation();

      const sensitivity = 0.001;
      const newScale = Math.max(0.5, Math.min(5, scale * (1 - event.deltaY * sensitivity)));

      if (newScale !== scale && onUpdate) {
        onUpdate({ scale: newScale });
      }
    },
    [hasImage, scale, onUpdate]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !hasImage) return;

    // Add wheel listener with passive: false to allow preventDefault.
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel, hasImage]);

  const rootClassName = className
    ? `${styles['visualiser-image-area']} ${className}`
    : styles['visualiser-image-area'];

  return (
    <div
      ref={containerRef}
      {...(hasImage ? bind() : {})}
      onMouseDown={() => hasImage && setActive(true)}
      onMouseUp={() => setActive(false)}
      onMouseLeave={() => setActive(false)}
      className={rootClassName}
      style={{
        position: 'absolute',
        top: position?.top,
        bottom: position?.bottom,
        left: position?.left,
        right: position?.right,
        width: size?.width || '90%',
        height: size?.height || '65%',
        backgroundColor,
        borderRadius,
        overflow: 'hidden',
        touchAction: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        cursor: hasImage ? (active ? 'grabbing' : 'grab') : 'default',
        ...style
      }}
    >
      {hasImage ? (
        <img
          className={styles['visualiser-image-area__image']}
          src={src}
          alt={alt}
          draggable={false}
          style={{
            transform: `translate3d(${x}px, ${y}px, 0) scale(${scale})`,
            maxWidth: 'none',
            maxHeight: 'none',
            pointerEvents: 'none',
            willChange: 'transform'
          }}
        />
      ) : (
        <div />
      )}
    </div>
  );
};



